/**
 * Lista de países emissores de passaporte, em ISO 3166-1 alpha-2.
 *
 * Os códigos saem do próprio ICU do navegador em vez de uma lista fixa no
 * repositório: assim os nomes já vêm no idioma do usuário e não há 250 linhas
 * de dados para alguém manter desatualizadas.
 */

/** Agregados, reservas e códigos aposentados que o ICU conhece mas não são países. */
const NOT_COUNTRIES = [
  'AC',
  'AN',
  'BU',
  'CP',
  'CS',
  'DD',
  'DG',
  'EA',
  'EU',
  'EZ',
  'FX',
  'IC',
  'NT',
  'QO',
  'QU',
  'SU',
  'TA',
  'TP',
  'UN',
  'XA',
  'XB',
  'YU',
  'ZR',
  'ZZ',
];

/**
 * Códigos antigos que o ICU resolve para o nome do país atual: `UK` devolve
 * "Reino Unido" igual a `GB`, `DY` devolve "Benin" igual a `BJ`. Oferecer os dois
 * punha duas opções de rótulo idêntico no select, e a escolha era sorteio.
 *
 * Não é só estética: a unicidade de passaporte é do par (número, país), então
 * o mesmo documento gravado uma vez como `UK` e outra como `GB` passaria pelo
 * índice como duas pessoas. O Apolo aceita só o código canônico.
 */
const SUPERSEDED = ['DY', 'HV', 'NH', 'RH', 'UK', 'VD', 'YD'];

const EXCLUDED = new Set([...NOT_COUNTRIES, ...SUPERSEDED]);

const FIRST = 'A'.charCodeAt(0);
const LAST = 'Z'.charCodeAt(0);

let cache = null;

function everyTwoLetterCode() {
  const codes = [];

  for (let a = FIRST; a <= LAST; a += 1) {
    for (let b = FIRST; b <= LAST; b += 1) {
      codes.push(String.fromCharCode(a) + String.fromCharCode(b));
    }
  }

  return codes;
}

/** O ICU devolve o próprio código quando não conhece a região. */
function nameFor(names, code) {
  try {
    const label = names.of(code);
    return label && label !== code ? label : null;
  } catch {
    return null;
  }
}

/**
 * @returns {{ value: string, label: string }[]} ordenado pelo nome, no locale
 * do navegador.
 */
export default function listCountries() {
  if (cache) return cache;

  const names = new Intl.DisplayNames(undefined, { type: 'region' });

  cache = everyTwoLetterCode()
    .filter(code => !EXCLUDED.has(code))
    .map(value => ({ value, label: nameFor(names, value) }))
    .filter(country => country.label)
    .sort((one, other) => one.label.localeCompare(other.label));

  return cache;
}
